/**
 * Utility functions for unified notification system
 * Bridges planner SSE events and agent_processing_status for TypingText component
 */

/**
 * Generate unified status for planner events
 * Used to convert planner SSE events into agent_processing_status format
 *
 * @param {string} planningStatus - The planning status from SSE event
 * @param {number} activeStep - Current active step number (1-indexed)
 * @param {number} totalSteps - Total number of steps in the plan
 * @returns {string} - Status string for TypingText component
 */
export const getPlannerUnifiedStatus = (
  planningStatus,
  activeStep,
  totalSteps
) => {
  // For step execution, use dynamic placeholder with step info
  // Dynamic placeholders are wrapped in ** and will be displayed as typed-out custom text
  if (
    planningStatus === "plan_execution_in_progress" &&
    activeStep > 0 &&
    totalSteps > 0
  ) {
    return `**Step ${activeStep} of ${totalSteps}**`;
  }
  // Return the status key directly - TypingText will look up rotating messages
  return planningStatus;
};

/**
 * Get input placeholder text for planner status
 * Used by ChatWebInput to show context-appropriate placeholder text
 *
 * @param {string} planningExecution - Current planning execution status
 * @param {boolean} isPlannerAcceptingFeedback - Whether planner accepts feedback during execution
 * @returns {string|null} - Placeholder text or null if no special placeholder needed
 */
export const getInputPlaceholderForPlannerStatus = (
  planningExecution,
  isPlannerAcceptingFeedback = false
) => {
  switch (planningExecution) {
    case "step_requires_approval":
      return "Type 'approve', 'okay' or 'reject' to respond to the step...";
    case "planning_requires_approval":
      return "Type 'approve', 'okay' or provide feedback on the plan...";
    case "step_requires_desktop":
      return "Desktop agent is executing the step...";
    case "plan_execution_in_progress":
      return isPlannerAcceptingFeedback
        ? "Provide feedback as the plan executes..."
        : null;
    default:
      return null;
  }
};
